import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type FormationMode = 'en_ligne' | 'presentiel' | null;

interface FormationModeContextType {
  mode: FormationMode;
  setMode: (mode: FormationMode) => void;
  hasChosen: boolean;
  showSplash: boolean;
  dismissSplash: () => void;
}

const FormationModeContext = createContext<FormationModeContextType | undefined>(undefined);

const STORAGE_KEY = 'formation_mode_preference';

export function FormationModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<FormationMode>(null);
  const [hasChosen, setHasChosen] = useState(false);
  const [showSplash, setShowSplash] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en_ligne' || stored === 'presentiel') {
      setModeState(stored);
      setHasChosen(true);
    }
    setShowSplash(true);
  }, []);

  const setMode = (newMode: FormationMode) => {
    setModeState(newMode);
    setHasChosen(true);
    setShowSplash(false);
    if (newMode) {
      localStorage.setItem(STORAGE_KEY, newMode);
    }
  };

  const dismissSplash = () => {
    setShowSplash(false);
  };

  return (
    <FormationModeContext.Provider value={{ mode, setMode, hasChosen, showSplash, dismissSplash }}>
      {children}
    </FormationModeContext.Provider>
  );
}

export function useFormationMode(): FormationModeContextType {
  const context = useContext(FormationModeContext);
  if (context === undefined) {
    throw new Error('useFormationMode must be used within a FormationModeProvider');
  }
  return context;
}